// web/src/components/consent/ConsentGate.tsx
//
// Blocks the authenticated app behind a clickwrap consent screen until the
// signed-in customer has accepted the CURRENT version of both the Privacy
// Policy and Terms of Service (versions from src/content/legal.ts). Fires
// for brand-new accounts (first login, nothing accepted yet) AND for
// existing accounts after a document version bump (re-consent) — a single
// gate at the app root covers both cases instead of only hooking the signup
// moment in GoogleLogin.
//
// Marketing opt-in is a separate, optional, unticked checkbox — never
// required to proceed, and never implied by ticking the required box.
import { useEffect, useState } from 'react';
import { PRIVACY_POLICY_VERSION, TERMS_VERSION } from '../../content/legal';
import { getConsentStatus, recordConsent, updateMarketingPrefs } from '../../services/consent-service';
import { ConsentCheckbox } from './ConsentCheckbox';

interface ConsentGateProps {
  children: React.ReactNode;
}

type GateState = 'checking' | 'needs-consent' | 'clear' | 'check-failed';

export function ConsentGate({ children }: ConsentGateProps) {
  const [state, setState] = useState<GateState>('checking');
  const [agreedRequired, setAgreedRequired] = useState(false);
  const [agreedMarketing, setAgreedMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // First acceptance vs re-consent. `accepted.privacy` is null when the user
  // has never accepted anything, so a null entry means signup and a non-null
  // entry with a stale version means a document bump.
  const [isFirstAcceptance, setIsFirstAcceptance] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getConsentStatus();
        if (cancelled) return;
        setIsFirstAcceptance(!status.accepted.privacy && !status.accepted.terms);
        const upToDate =
          status.accepted.privacy?.version === PRIVACY_POLICY_VERSION &&
          status.accepted.terms?.version === TERMS_VERSION;
        setState(upToDate ? 'clear' : 'needs-consent');
      } catch (e) {
        // Fail OPEN, not closed: a transient consent-status outage must not
        // hard-lock every signed-in customer out of the app. Legal risk of a
        // rare unlogged acceptance is far smaller than bricking the product.
        console.warn('[consent] status check failed, allowing through', e);
        if (!cancelled) setState('check-failed');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit() {
    if (!agreedRequired || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await Promise.all([
        recordConsent('privacy', PRIVACY_POLICY_VERSION),
        recordConsent('terms', TERMS_VERSION),
      ]);
      if (agreedMarketing) {
        // Non-fatal: the required consent above is what gates entry. A
        // marketing-prefs hiccup shouldn't strand the user on this screen.
        await updateMarketingPrefs({ marketingEmailOptIn: true, marketingPushOptIn: true }).catch((e) =>
          console.warn('[consent] marketing prefs failed', e),
        );
      }
      setState('clear');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your acceptance — try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (state === 'checking') {
    return (
      <div className="min-h-dvh bg-coral flex items-center justify-center safe-shell">
        <p className="font-grotesk font-bold text-lg text-white animate-pulse">LOADING…</p>
      </div>
    );
  }

  if (state === 'clear' || state === 'check-failed') {
    return <>{children}</>;
  }

  return (
    <div className="min-h-dvh bg-coral flex items-center justify-center p-4 md:p-8 safe-shell">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-[16px] p-6 md:p-8">
          <h1 className="font-grotesk font-bold text-xl text-primary mb-2 text-center">
            {isFirstAcceptance ? 'WELCOME TO BIT BREW' : 'BEFORE YOU CONTINUE'}
          </h1>
          <p className="font-grotesk font-medium text-[13px] text-body-grey mb-6 text-center">
            {isFirstAcceptance
              ? 'Just one thing before you start — please review and accept our terms.'
              : 'We updated our legal terms. Please review and accept to keep using Bit Brew.'}
          </p>

          <div className="space-y-4 mb-6">
            <ConsentCheckbox
              id="consent-required"
              checked={agreedRequired}
              onChange={setAgreedRequired}
              required
              label={
                <>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-coral underline">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-coral underline">
                    Privacy Policy
                  </a>
                  . (required)
                </>
              }
            />
            <ConsentCheckbox
              id="consent-marketing"
              checked={agreedMarketing}
              onChange={setAgreedMarketing}
              label="Send me emails and app notifications about news, offers and updates. (optional — you can change this anytime)"
            />
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-[10px] border-2 border-error bg-error/10">
              <p className="font-grotesk font-medium text-[12px] text-error">{error}</p>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!agreedRequired || submitting}
            className="w-full rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 disabled:opacity-50"
          >
            {submitting ? 'SAVING…' : 'CONTINUE'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsentGate;
